import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import minMax from 'dayjs/plugin/minMax'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useMemo } from 'react'
import { CalendarShiftData, PlanningViewMode } from './types'
import { Shift, ShiftTemplate } from '@/core/models/shift.model'
import { ScheduleRecurrenceType } from '@/core/models/schedule.model'
import { ShiftException } from '@/core/api/planning'
import { Planning } from '@/core/models/planning.model'

dayjs.extend(minMax)
dayjs.extend(isoWeek)
dayjs.extend(utc)
dayjs.extend(timezone)

type UsePlanningCalendarProps = {
  currentDate: Date
  planning: Planning
  shifts: Shift[]
  shiftExceptions: ShiftException[]
  shiftTemplates: ShiftTemplate[]
  viewMode: PlanningViewMode
}

export function usePlanningCalendar({ currentDate, planning, shifts, shiftExceptions, shiftTemplates, viewMode }: UsePlanningCalendarProps) {
  const schedulerData = useMemo(() => {
    let data: CalendarShiftData[] = []
    const shiftData = shifts.map((shift) => formatShiftData(shift))
    const shiftTemplateData = shiftTemplates.flatMap((template) => formatShiftTemplateData(template, shifts, currentDate))

    switch (viewMode) {
      case 'all':
        data = [...shiftData, ...shiftTemplateData]
        break
      case 'shifts':
        data = shiftData
        break
      case 'templates':
        data = shiftTemplateData
        break
      default:
        break
    }
    // Shift/slot start times are true instants, so bound them by the END OF the
    // planning's last calendar day on the viewer's local clock. Comparing against the
    // raw planning.endDate instant (which is stored at an arbitrary time-of-day)
    // wrongly hid every slot after that time on the last day — e.g. all afternoon
    // shifts when endDate landed around noon.
    const planningEnd = dayjs(dayjs.utc(planning.endDate).format('YYYY-MM-DD')).endOf('day').toDate()
    return data.filter((template) => template.startDate <= planningEnd && rejectShiftException(shiftExceptions, template))
  }, [shifts, shiftExceptions, shiftTemplates, currentDate, viewMode, planning.endDate])

  const statistics = useMemo(() => {
    const totalShifts = shifts.length

    // Calculate total required staff across all shifts
    const totalRequiredStaff = shifts.reduce((total, shift) => {
      const requiredStaffCount = shift.shiftTemplate?.requiredStaffCount || 1
      return total + requiredStaffCount
    }, 0)

    // Calculate total assigned staff
    const totalAssignedStaff = shifts.reduce((total, shift) => {
      return total + (shift.assignedStaff?.length || 0)
    }, 0)

    const filledShifts = shifts.filter((shift) => shift.assignedStaff && shift.assignedStaff.length >= (shift.shiftTemplate.requiredStaffCount || 0)).length

    const partiallyFilledShifts = shifts.filter(
      (shift) => shift.assignedStaff && shift.assignedStaff.length > 0 && shift.assignedStaff.length < (shift.shiftTemplate.requiredStaffCount || 0)
    ).length

    const emptyShifts = shifts.filter((shift) => !shift.assignedStaff || shift.assignedStaff.length === 0).length

    // Calculate fill rate based on assigned staff vs required staff
    const fillRate = totalRequiredStaff > 0 ? Math.min(100, Math.round((totalAssignedStaff / totalRequiredStaff) * 100)) : 0

    return {
      totalShifts,
      filledShifts,
      partiallyFilledShifts,
      emptyShifts,
      fillRate,
      totalRequiredStaff,
      totalAssignedStaff,
    }
  }, [shifts])

  return { schedulerData, statistics }
}

function formatShiftData(shift: Shift): CalendarShiftData {
  return {
    id: shift._id,
    startDate: new Date(shift.startTime),
    endDate: new Date(shift.endTime),
    title: shift.shiftTemplate.location,
    type: 'shift',
    color: '#4CAF50',
    isPast: dayjs(shift.endTime).isBefore(dayjs()),
    isStaffed: shift.assignedStaff?.length > 0,
    location: shift.shiftTemplate.location,
    assignedStaff: shift.assignedStaff,
    remainingSlots: (shift.shiftTemplate.requiredStaffCount || 0) - shift.assignedStaff?.length || 0,
    requiredStaffCount: shift.shiftTemplate.requiredStaffCount || 0,
    consultationReason: shift.shiftTemplate.consultationReason,
  }
}

/**
 * Materialize a template occurrence on a calendar day as a true instant, mirroring the
 * backend slot generators: minutes are wall-clock in template.timezone (legacy templates
 * without one: minutes-from-UTC-midnight). The scheduler then renders it in the
 * viewer's local time like any concrete shift.
 */
function materializeOccurrenceTime(day: Dayjs, minutes: number, tz?: string): Dayjs {
  if (!tz) return day.startOf('day').utc(true).add(minutes, 'minutes')
  const hours = Math.floor(minutes / 60) % 24
  const dayRollover = Math.floor(minutes / (24 * 60))
  const wallClock = `${day.add(dayRollover, 'day').format('YYYY-MM-DD')} ${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
  return dayjs.tz(wallClock, tz)
}

type ShiftOccurrenceIdentity = {
  startTime: string
  shiftTemplate: { _id: string }
}

export function hasConcreteShiftForTemplateOccurrence(shifts: ShiftOccurrenceIdentity[], shiftTemplateId: string, occurrenceStart: Date): boolean {
  const occurrenceMinute = Math.floor(occurrenceStart.getTime() / 60000)
  return shifts.some(
    (shift) => shift.shiftTemplate._id === shiftTemplateId && Math.floor(new Date(shift.startTime).getTime() / 60000) === occurrenceMinute
  )
}

function buildTemplateOccurrence(template: ShiftTemplate, day: Dayjs, shifts: Shift[]): CalendarShiftData | null {
  const startTime = materializeOccurrenceTime(day, template.startTime, template.timezone)
  const endTime = materializeOccurrenceTime(day, template.endTime, template.timezone)

  // Hide only the occurrence represented by a concrete shift from this same template.
  // Other templates may intentionally overlap for replacements or backup capacity.
  const hasShift = hasConcreteShiftForTemplateOccurrence(shifts, template._id, startTime.toDate())
  if (hasShift) return null

  return {
    id: `template-${template._id}-${day.format('YYYY-MM-DD')}`,
    startDate: startTime.toDate(),
    endDate: endTime.toDate(),
    title: template.location,
    type: 'shiftTemplate',
    color: '#2196F3',
    isPast: endTime.isBefore(dayjs()),
    isShiftCreated: false,
    location: template.location,
    requiredStaffCount: template.requiredStaffCount,
    recurrenceRule: template.recurrenceRule,
    shiftTemplateId: template._id,
  }
}

function formatShiftTemplateData(template: ShiftTemplate, shifts: Shift[], date: Date) {
  const startOfWeek = dayjs(date).startOf('isoWeek')
  const endOfWeek = startOfWeek.add(1, 'week')

  // Non-recurring (one-off) template: a single occurrence on its specific date.
  if (!template.recurrenceRule) {
    if (!template.startDate) return []
    const occDay = dayjs(template.startDate)
    if (occDay.isBefore(startOfWeek) || !occDay.isBefore(endOfWeek)) return []
    const occurrence = buildTemplateOccurrence(template, occDay, shifts)
    return occurrence ? [occurrence] : []
  }

  // Recurring template: emit a matching occurrence for each day of the visible week.
  const templateOccurrences: CalendarShiftData[] = []
  let currentDate = startOfWeek
  while (currentDate.isBefore(endOfWeek)) {
    if (isDateMatchingRecurrenceRule(currentDate, template.recurrenceRule, startOfWeek)) {
      const occurrence = buildTemplateOccurrence(template, currentDate, shifts)
      if (occurrence) templateOccurrences.push(occurrence)
    }
    currentDate = currentDate.add(1, 'day')
  }
  return templateOccurrences
}

function isDateMatchingRecurrenceRule(date: Dayjs, recurrenceRule: ShiftTemplate['recurrenceRule'], weekStart: dayjs.Dayjs) {
  if (!recurrenceRule) {
    return false
  }

  const { daysOfWeek, recurrenceType, separationCount } = recurrenceRule

  // Check if the date is in the selected week's daysOfWeek
  const isDayInDaysOfWeek = daysOfWeek?.includes(date.day())

  if (!isDayInDaysOfWeek) return false

  if (recurrenceType === ScheduleRecurrenceType.DAILY) {
    return true
  } else if (recurrenceType === ScheduleRecurrenceType.WEEKLY) {
    // Calculate weeks since the start of the current view
    const weeksSinceStart = date.diff(weekStart, 'week')
    const isMatchingWeek = weeksSinceStart % separationCount === 0
    return isMatchingWeek
  }

  return false
}

function rejectShiftException(shiftExceptions: ShiftException[], data: CalendarShiftData) {
  return !shiftExceptions.find(
    (shiftException) =>
      shiftException.shiftTemplate === data.shiftTemplateId &&
      shiftException.startTime === data.startDate.toISOString() &&
      shiftException.endTime === data.endDate.toISOString()
  )
}
